import { Suspense, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Copy, Link2, Loader2, PlayCircle, Search } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  PRONTIDAO_LABEL, SITUACAO_CONSULTA_LABEL, TIPO_ITEM_LABEL,
  type Clinica, type SituacaoConsulta, type StatusProntidao, type TipoConsulta,
} from '@/lib/types'
import { hojeISO } from '@/lib/datas'
import { listClinicas } from '@/services/clinicas'
import { listPacientes } from '@/services/pacientes'
import {
  criarConsulta, getConsulta, atualizarConsulta, mudarSituacaoConsulta,
} from '@/services/consultas'
import {
  concluirItem, dispensarItem, gerarLinkPreAnamnese, getPreparacao,
  iniciarPreparacao, motivosPendentes, reabrirItem, type Preparacao,
} from '@/services/preparacoes'
import {
  listExamesDoPaciente, mudarStatusExame, resumoRetorno, solicitarExame,
  STATUS_EXAME_LABEL, type ExameSolicitado,
} from '@/services/exames'
import {
  autorizarExcecao, COMPORTAMENTO_LABEL, EXIGENCIA_LABEL, listSolicitacoesDoPaciente,
  listTermoModelos, revogarSolicitacao, solicitarTermo, STATUS_TERMO_LABEL,
  type ComportamentoTermo, type ExigenciaTermo, type TermoModeloComVersao, type TermoSolicitacao,
} from '@/services/termos'

function FormConsulta() {
  const { current, papel } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const editId = params.get('id')
  const pacientePre = params.get('paciente')

  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [pacienteId, setPacienteId] = useState(pacientePre ?? '')
  const [pacienteNome, setPacienteNome] = useState('')
  const [buscaPaciente, setBuscaPaciente] = useState('')
  const [sugestoes, setSugestoes] = useState<{ id: string; nome: string }[]>([])
  const [clinicaId, setClinicaId] = useState('')
  const [data, setData] = useState(hojeISO())
  const [horaInicio, setHoraInicio] = useState('09:00')
  const [horaFim, setHoraFim] = useState('')
  const [tipo, setTipo] = useState<TipoConsulta>('primeira')
  const [motivo, setMotivo] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [situacao, setSituacao] = useState<SituacaoConsulta>('agendada')
  const [prontidao, setProntidao] = useState<StatusProntidao>('nao_iniciada')
  const [prep, setPrep] = useState<Preparacao | null>(null)
  const [exames, setExames] = useState<ExameSolicitado[]>([])
  const [novoExame, setNovoExame] = useState('')
  const [termoModelos, setTermoModelos] = useState<TermoModeloComVersao[]>([])
  const [solicitacoes, setSolicitacoes] = useState<TermoSolicitacao[]>([])
  const [termoModeloSel, setTermoModeloSel] = useState('')
  const [termoComportamento, setTermoComportamento] = useState<ComportamentoTermo>('assinatura')
  const [termoExigencia, setTermoExigencia] = useState<ExigenciaTermo>('obrigatorio')
  const [linkGerado, setLinkGerado] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const carregar = useCallback(async () => {
    if (!current) return
    try {
      const cs = await listClinicas(current.workspace.id)
      setClinicas(cs.filter(c => c.ativo))
      if (editId) {
        const c = await getConsulta(current.workspace.id, editId)
        if (c) {
          setPacienteId(c.paciente_id); setPacienteNome(c.paciente_nome)
          setClinicaId(c.clinica_id ?? ''); setData(c.data)
          setHoraInicio(c.hora_inicio.slice(0, 5)); setHoraFim(c.hora_fim?.slice(0, 5) ?? '')
          setTipo(c.tipo); setMotivo(c.motivo ?? ''); setObservacoes(c.observacoes ?? '')
          setSituacao(c.situacao)
          setProntidao(c.status_prontidao)
          try {
            setPrep(await getPreparacao(current.workspace.id, c.id))
          } catch { /* phase4 ainda não aplicada */ }
          try {
            setExames(await listExamesDoPaciente(current.workspace.id, c.paciente_id))
          } catch { /* phase5 ainda não aplicada */ }
          try {
            const [tms, sols] = await Promise.all([
              listTermoModelos(current.workspace.id),
              listSolicitacoesDoPaciente(current.workspace.id, c.paciente_id),
            ])
            setTermoModelos(tms)
            setSolicitacoes(sols)
          } catch { /* phase6 ainda não aplicada */ }
        }
      } else if (pacientePre) {
        const ps = await listPacientes(current.workspace.id, {})
        const p = ps.find(x => x.id === pacientePre)
        if (p) setPacienteNome(p.nome)
      }
    } finally {
      setLoading(false)
    }
  }, [current, editId, pacientePre])

  useEffect(() => { carregar() }, [carregar])

  // autocomplete de paciente
  useEffect(() => {
    if (!current || buscaPaciente.trim().length < 2) { setSugestoes([]); return }
    const t = setTimeout(async () => {
      const ps = await listPacientes(current.workspace.id, { busca: buscaPaciente, situacao: 'todas' })
      setSugestoes(ps.slice(0, 8).map(p => ({ id: p.id, nome: p.nome })))
    }, 250)
    return () => clearTimeout(t)
  }, [current, buscaPaciente])

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    if (!current) return
    if (!pacienteId) { setErro('Selecione o paciente.'); return }
    if (!data) { setErro('Informe a data da consulta.'); return }
    if (!horaInicio) { setErro('Informe o horário de início.'); return }
    if (!tipo) { setErro('Selecione o tipo da consulta.'); return }
    setBusy(true)
    setErro(null)
    const dados = {
      paciente_id: pacienteId,
      clinica_id: clinicaId || null,
      data,
      hora_inicio: horaInicio,
      hora_fim: horaFim || null,
      tipo,
      motivo: motivo || null,
      observacoes: observacoes || null,
    }
    try {
      if (editId) {
        await atualizarConsulta({ workspaceId: current.workspace.id, id: editId, pacienteId, dados })
      } else {
        const novaId = await criarConsulta({ workspaceId: current.workspace.id, dados })
        // inicia a jornada de preparação se houver protocolo aplicável (phase4)
        try {
          await iniciarPreparacao({
            workspaceId: current.workspace.id,
            consultaId: novaId,
            pacienteId,
            tipo,
          })
        } catch { /* sem protocolo ou phase4 não aplicada — segue sem jornada */ }
      }
      navigate('/agenda')
    } catch {
      setErro('Não foi possível salvar a consulta.')
      setBusy(false)
    }
  }

  async function recarregarPrep() {
    if (!current || !editId) return
    setPrep(await getPreparacao(current.workspace.id, editId))
    const c = await getConsulta(current.workspace.id, editId)
    if (c) setProntidao(c.status_prontidao)
  }

  async function iniciarJornada() {
    if (!current || !editId || !pacienteId) return
    setBusy(true)
    setErro(null)
    try {
      const r = await iniciarPreparacao({
        workspaceId: current.workspace.id,
        consultaId: editId,
        pacienteId,
        tipo,
      })
      if (!r) setErro('Nenhum protocolo publicado se aplica a este tipo de consulta. Crie um em Protocolos.')
      await recarregarPrep()
    } finally {
      setBusy(false)
    }
  }

  async function mudarSituacao(nova: SituacaoConsulta) {
    if (!current || !editId) return
    setBusy(true)
    try {
      await mudarSituacaoConsulta({ workspaceId: current.workspace.id, id: editId, pacienteId, situacao: nova })
      setSituacao(nova)
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
      </div>
    )
  }

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 16 }}>{editId ? 'Consulta' : 'Nova consulta'}</h1>

      <form onSubmit={salvar} className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label className="field-label" htmlFor="c-paciente">Paciente *</label>
          {pacienteId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="badge badge-info" style={{ fontSize: 13.5, padding: '8px 12px' }}>{pacienteNome}</span>
              {!editId && (
                <button type="button" className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 13 }} onClick={() => { setPacienteId(''); setPacienteNome('') }}>
                  Trocar
                </button>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <Search size={16} aria-hidden style={{ position: 'absolute', left: 12, top: 14, color: 'var(--muted)' }} />
              <input
                id="c-paciente" className="field-input" style={{ paddingLeft: 36 }}
                placeholder="Digite o nome do paciente"
                value={buscaPaciente} onChange={e => setBuscaPaciente(e.target.value)}
                autoComplete="off"
              />
              {sugestoes.length > 0 && (
                <div className="card" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, top: 48, boxShadow: 'var(--shadow-float)', overflow: 'hidden' }}>
                  {sugestoes.map(s => (
                    <button
                      key={s.id} type="button"
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', background: 'none', border: 'none', fontSize: 14, borderBottom: '1px solid var(--border)' }}
                      onClick={() => { setPacienteId(s.id); setPacienteNome(s.nome); setSugestoes([]); setBuscaPaciente('') }}
                    >
                      {s.nome}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="c-data">Data *</label>
            <input id="c-data" type="date" required className="field-input" value={data} onChange={e => setData(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="c-hora">Início *</label>
            <input id="c-hora" type="time" required className="field-input" value={horaInicio} onChange={e => setHoraInicio(e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="c-fim">Fim</label>
            <input id="c-fim" type="time" className="field-input" value={horaFim} onChange={e => setHoraFim(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="field-label" htmlFor="c-clinica">Clínica</label>
            <select id="c-clinica" className="field-input" value={clinicaId} onChange={e => setClinicaId(e.target.value)}>
              <option value="">Sem clínica definida</option>
              {clinicas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label" htmlFor="c-tipo">Tipo *</label>
            <select id="c-tipo" required className="field-input" value={tipo} onChange={e => setTipo(e.target.value as TipoConsulta)}>
              <option value="primeira">Primeira consulta</option>
              <option value="retorno">Retorno</option>
            </select>
          </div>
        </div>

        <div>
          <label className="field-label" htmlFor="c-motivo">Motivo</label>
          <input id="c-motivo" className="field-input" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex.: rotina anual, resultado de exames" />
        </div>
        <div>
          <label className="field-label" htmlFor="c-obs">Observações</label>
          <textarea id="c-obs" className="field-input" rows={2} style={{ resize: 'vertical' }} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
        </div>

        {editId && (
          <div>
            <span className="field-label">Situação: <strong>{SITUACAO_CONSULTA_LABEL[situacao]}</strong></span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(['confirmada', 'realizada', 'cancelada', 'faltou'] as SituacaoConsulta[]).filter(s => s !== situacao).map(s => (
                <button key={s} type="button" className="btn-secondary" style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }} disabled={busy} onClick={() => mudarSituacao(s)}>
                  Marcar {SITUACAO_CONSULTA_LABEL[s].toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        {erro && <div className="field-error" role="alert">{erro}</div>}

      {editId && (
        <section className="card" style={{ padding: 18, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <strong style={{ fontSize: 15 }}>Preparação da consulta</strong>
            <span className={`badge ${prontidao === 'pronta' ? 'badge-ok' : prontidao === 'bloqueada' ? 'badge-danger' : prontidao === 'nao_iniciada' ? 'badge-muted' : 'badge-warn'}`}>
              {PRONTIDAO_LABEL[prontidao]}
            </span>
          </div>

          {!prep ? (
            <div>
              <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 10px' }}>
                A jornada de preparação ainda não foi iniciada para esta consulta.
              </p>
              <button className="btn-secondary" disabled={busy} onClick={iniciarJornada}>
                <PlayCircle size={15} aria-hidden /> Iniciar preparação
              </button>
            </div>
          ) : (
            <>
              {motivosPendentes(prep).length > 0 && prontidao !== 'pronta' && (
                <div className="badge badge-warn" style={{ marginBottom: 10, fontSize: 12.5, display: 'block', padding: '8px 12px' }}>
                  Falta: {motivosPendentes(prep).join(' · ')}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {prep.itens.map(item => (
                  <div key={item.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{item.titulo}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {TIPO_ITEM_LABEL[item.tipo]}
                          {item.bloqueia ? ' · bloqueia a consulta' : item.obrigatorio ? ' · obrigatório' : ' · opcional'}
                          {item.respondido_por === 'paciente' ? ' · respondido pela paciente' : ''}
                          {item.dispensado_motivo ? ` · dispensado: ${item.dispensado_motivo}` : ''}
                        </div>
                      </div>
                      <span className={`badge ${item.status === 'concluido' ? 'badge-ok' : item.status === 'dispensado' ? 'badge-muted' : 'badge-warn'}`}>
                        {item.status === 'concluido' ? 'Concluído' : item.status === 'dispensado' ? 'Dispensado' : 'Pendente'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {item.status === 'pendente' && (
                        <>
                          {item.tipo === 'questionario' && (
                            <button
                              className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                              disabled={busy}
                              onClick={async () => {
                                if (!current) return
                                setBusy(true)
                                try {
                                  const path = await gerarLinkPreAnamnese({
                                    workspaceId: current.workspace.id,
                                    pacienteId,
                                    consultaId: editId,
                                    preparacaoItemId: item.id,
                                  })
                                  setLinkGerado(`${window.location.origin}${path}`)
                                } finally { setBusy(false) }
                              }}
                            >
                              <Link2 size={13} aria-hidden /> Link para a paciente
                            </button>
                          )}
                          <button
                            className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                            disabled={busy}
                            onClick={async () => {
                              if (!current) return
                              await concluirItem({ workspaceId: current.workspace.id, item, pacienteId, consultaId: editId })
                              await recarregarPrep()
                            }}
                          >
                            Marcar concluído
                          </button>
                          <button
                            className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                            disabled={busy}
                            onClick={async () => {
                              if (!current) return
                              const motivoD = window.prompt('Motivo da dispensa (obrigatório):')
                              if (!motivoD?.trim()) return
                              await dispensarItem({ workspaceId: current.workspace.id, item, motivo: motivoD, pacienteId, consultaId: editId })
                              await recarregarPrep()
                            }}
                          >
                            Dispensar
                          </button>
                        </>
                      )}
                      {item.status !== 'pendente' && (
                        <button
                          className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                          disabled={busy}
                          onClick={async () => {
                            if (!current) return
                            await reabrirItem({ workspaceId: current.workspace.id, itemId: item.id })
                            await recarregarPrep()
                          }}
                        >
                          Reabrir
                        </button>
                      )}
                    </div>
                    {item.tipo === 'questionario' && item.respostas && Object.keys(item.respostas).length > 0 && (
                      <details style={{ marginTop: 8, fontSize: 13 }}>
                        <summary style={{ cursor: 'pointer', color: 'var(--primary-deep)', fontWeight: 600 }}>Ver respostas</summary>
                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {Object.entries(item.respostas).map(([k, v]) => (
                            <div key={k} style={{ color: 'var(--text-secondary)' }}>
                              <code style={{ fontSize: 11.5, color: 'var(--muted)' }}>{k}</code>: {Array.isArray(v) ? v.join(', ') : String(v)}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
                {prep.itens.length === 0 && (
                  <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>O protocolo aplicado não tem itens.</div>
                )}
              </div>
              {linkGerado && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                  <code style={{ fontSize: 12, background: 'var(--bg)', padding: '8px 10px', borderRadius: 8, wordBreak: 'break-all', flex: 1, minWidth: 200 }}>
                    {linkGerado}
                  </code>
                  <button
                    type="button" className="btn-secondary" style={{ minHeight: 36 }}
                    onClick={async () => { await navigator.clipboard.writeText(linkGerado); setCopiado(true); setTimeout(() => setCopiado(false), 2000) }}
                  >
                    {copiado ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
                    {copiado ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {editId && (
        <section className="card" style={{ padding: 18, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <strong style={{ fontSize: 15 }}>Exames</strong>
            {tipo === 'retorno' && (() => {
              const r = resumoRetorno(exames)
              return (
                <span className={`badge ${r.pronto === 'pronto' ? 'badge-ok' : r.pronto === 'sem_exames' ? 'badge-muted' : r.pronto === 'parcial' ? 'badge-warn' : 'badge-danger'}`}>
                  {r.pronto === 'pronto' ? 'Retorno pronto — exames completos'
                    : r.pronto === 'sem_exames' ? 'Sem exames vinculados'
                    : `Retorno ${r.pronto === 'parcial' ? 'parcialmente pronto' : 'não pronto'} — ${r.recebidos}/${r.total} recebidos`}
                </span>
              )
            })()}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <input
              className="field-input" style={{ flex: 1, minWidth: 180, minHeight: 40 }}
              placeholder="Solicitar exame (ex.: Mamografia bilateral)"
              value={novoExame} onChange={e => setNovoExame(e.target.value)}
              aria-label="Nome do exame"
            />
            <button
              className="btn-secondary" disabled={busy || !novoExame.trim()}
              onClick={async () => {
                if (!current) return
                await solicitarExame({ workspaceId: current.workspace.id, pacienteId, consultaId: editId, nome: novoExame })
                setNovoExame('')
                setExames(await listExamesDoPaciente(current.workspace.id, pacienteId))
              }}
            >
              Solicitar
            </button>
          </div>

          {exames.length === 0 ? (
            <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>Nenhum exame solicitado para esta paciente.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {exames.map(ex => (
                <div key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{ex.nome}</span>
                  <span className={`badge ${ex.status === 'recebido' || ex.status === 'analisado' ? 'badge-ok' : ex.status === 'cancelado' ? 'badge-muted' : 'badge-warn'}`}>
                    {STATUS_EXAME_LABEL[ex.status]}
                  </span>
                  {(ex.status === 'solicitado' || ex.status === 'realizado') && (
                    <>
                      {ex.status === 'solicitado' && (
                        <button
                          className="btn-secondary" style={{ minHeight: 30, padding: '2px 8px', fontSize: 12 }}
                          onClick={async () => {
                            if (!current) return
                            await mudarStatusExame({ workspaceId: current.workspace.id, exame: ex, status: 'realizado' })
                            setExames(await listExamesDoPaciente(current.workspace.id, pacienteId))
                          }}
                        >
                          Realizado
                        </button>
                      )}
                      <button
                        className="btn-secondary" style={{ minHeight: 30, padding: '2px 8px', fontSize: 12 }}
                        onClick={async () => {
                          if (!current) return
                          await mudarStatusExame({ workspaceId: current.workspace.id, exame: ex, status: 'recebido' })
                          setExames(await listExamesDoPaciente(current.workspace.id, pacienteId))
                        }}
                      >
                        Recebido
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {editId && (
        <section className="card" style={{ padding: 18, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <strong style={{ fontSize: 15 }}>Termos e consentimentos</strong>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <select
              className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5, flex: 1, minWidth: 180 }}
              value={termoModeloSel} onChange={e => setTermoModeloSel(e.target.value)}
              aria-label="Modelo de termo"
            >
              <option value="">Escolher termo…</option>
              {termoModelos.filter(m => m.versao_atual).map(m => (
                <option key={m.id} value={m.versao_atual!.id}>{m.nome} (v{m.versao_atual!.numero})</option>
              ))}
            </select>
            <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={termoComportamento} onChange={e => setTermoComportamento(e.target.value as ComportamentoTermo)} aria-label="Comportamento">
              {(Object.keys(COMPORTAMENTO_LABEL) as ComportamentoTermo[]).map(c => (
                <option key={c} value={c}>{COMPORTAMENTO_LABEL[c]}</option>
              ))}
            </select>
            <select className="field-input" style={{ width: 'auto', minHeight: 38, fontSize: 13.5 }} value={termoExigencia} onChange={e => setTermoExigencia(e.target.value as ExigenciaTermo)} aria-label="Exigência">
              {(Object.keys(EXIGENCIA_LABEL) as ExigenciaTermo[]).map(x => (
                <option key={x} value={x}>{EXIGENCIA_LABEL[x]}</option>
              ))}
            </select>
            <button
              className="btn-secondary" style={{ minHeight: 38, fontSize: 13 }}
              disabled={busy || !termoModeloSel}
              onClick={async () => {
                if (!current || !termoModeloSel) return
                setBusy(true)
                try {
                  const { path } = await solicitarTermo({
                    workspaceId: current.workspace.id,
                    pacienteId,
                    consultaId: editId,
                    termoVersaoId: termoModeloSel,
                    exigencia: termoExigencia,
                    comportamento: termoComportamento,
                  })
                  setLinkGerado(`${window.location.origin}${path}`)
                  setSolicitacoes(await listSolicitacoesDoPaciente(current.workspace.id, pacienteId))
                  await recarregarPrep()
                } finally { setBusy(false) }
              }}
            >
              Enviar termo
            </button>
          </div>

          {solicitacoes.filter(s => s.consulta_id === editId).length === 0 ? (
            <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>Nenhum termo solicitado nesta consulta.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {solicitacoes.filter(s => s.consulta_id === editId).map(s => (
                <div key={s.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                    <span className="badge badge-muted">{EXIGENCIA_LABEL[s.exigencia]}</span>{' '}
                    <span className="badge badge-muted">{COMPORTAMENTO_LABEL[s.comportamento]}</span>
                    {s.excecao_autorizada && (
                      <span className="badge badge-warn" style={{ marginLeft: 4 }}>Exceção: {s.excecao_justificativa}</span>
                    )}
                  </div>
                  <span className={`badge ${s.status === 'aceito' || s.status === 'assinado' ? 'badge-ok' : s.status === 'recusado' ? 'badge-danger' : 'badge-info'}`}>
                    {STATUS_TERMO_LABEL[s.status]}
                  </span>
                  {!['aceito', 'assinado', 'revogado', 'substituido'].includes(s.status) && (
                    <>
                      <button
                        className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                        disabled={busy}
                        onClick={async () => {
                          if (!current) return
                          await revogarSolicitacao(current.workspace.id, s)
                          setSolicitacoes(await listSolicitacoesDoPaciente(current.workspace.id, pacienteId))
                          await recarregarPrep()
                        }}
                      >
                        Revogar
                      </button>
                      {s.comportamento === 'bloquear' && !s.excecao_autorizada && (papel === 'medico_titular' || papel === 'medico_colaborador') && (
                        <button
                          className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                          disabled={busy}
                          onClick={async () => {
                            if (!current) return
                            const j = window.prompt('Justificativa da exceção (obrigatória):')
                            if (!j?.trim()) return
                            await autorizarExcecao(current.workspace.id, s, j)
                            setSolicitacoes(await listSolicitacoesDoPaciente(current.workspace.id, pacienteId))
                            await recarregarPrep()
                          }}
                        >
                          Autorizar exceção
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
            Salvar
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Voltar</button>
        </div>
      </form>
    </div>
  )
}

export default function NovaConsultaPage() {
  // useSearchParams exige Suspense no build de produção
  return (
    <Suspense fallback={null}>
      <FormConsulta />
    </Suspense>
  )
}
